<template>
  <div class="pdv-exit-report-page">
    <!-- Loading -->
    <div v-if="isPageLoading" class="loading-state">
      <i class="pi pi-spinner pi-spin"></i>
      <span>{{ t('Carregando laudo de saída...') }}</span>
    </div>

    <template v-else>
      <!-- Header: item info -->
      <div class="report-header" v-if="reportData">
        <div class="header-info">
          <h3 v-if="!embedded">{{ t('Laudo de Saída') }}</h3>
          <div class="item-info">
            <p v-if="reportData.qr_code" data-testid="exit-report-qr-code">
              <strong>{{ t('QR Code') }}:</strong> {{ reportData.qr_code }}
            </p>
            <p v-if="reportData.description" data-testid="exit-report-item">
              <strong>{{ t('Item') }}:</strong> {{ reportData.description }}
            </p>
            <p v-if="reportData.client_name" data-testid="exit-report-client">
              <strong>{{ t('Cliente') }}:</strong> {{ reportData.client_name }}
            </p>
          </div>
        </div>
        <div class="header-actions">
          <button
            v-if="effectiveOrderItemId && analiseLaudosEnabled"
            type="button"
            class="btn btn-default btn-compare"
            data-testid="exit-report-view-entry-laudo"
            @click="openComparisonModal"
          >
            <i class="pi pi-eye"></i> {{ t('Ver Laudo de Entrada') }}
          </button>
          <Tag
            v-if="reportData.status"
            :value="reportData.status === 'confirmed' ? t('Confirmado') : t('Pendente')"
            :severity="reportData.status === 'confirmed' ? 'success' : 'warn'"
          />
        </div>
      </div>

      <!-- Dynamic Report Attributes Section -->
      <SOReportAttributesList
        v-if="reportAttributes.length > 0"
        v-model:attributes="reportAttributes"
        :editable="reportAttributesEditable"
        :order-item-id="effectiveOrderItemId"
        :editable-locked="editToggleDisabled"
        :locked-tooltip="t('Edição bloqueada: existem etapas de produção pendentes para este item')"
        test-id-prefix="so-exit-report-laudo-attr"
        @update:editable="handleEditableUpdate"
      />

      <!-- Reprocesso Section: foto-depois upload happens here as part of the exit-laudo verification -->
      <SOReprocessoSection
        :order-item-id="Number(effectiveOrderItemId) || 0"
        :completed-steps="completedSteps"
        mode="verify"
        :editable="reprocessosEditable"
        style="margin-top: 16px;"
      />
      <small
        v-if="hasPendingReprocessos"
        class="confirm-hint"
        data-testid="exit-report-reprocesso-pending-hint"
      >
        {{ t('Adicione a foto depois de cada reprocesso pendente para confirmar o laudo de saída') }}
      </small>

      <!-- Order Item Requests Verifier Section. Renders inline in both
           standalone and embedded modes — canConfirm gates on
           !hasUnverifiedRequests, so the user needs a visible UI to verify
           pending requests wherever the exit report is shown. -->
      <box
        v-if="requests.length > 0"
        :title="t('Solicitações do Cliente')"
        style="margin-top: 16px;"
        data-testid="exit-report-requests-box"
      >
        <SOOrderItemRequestsVerifier
          :requests="requests"
          context="exit"
          :editable="reprocessosEditable"
          @request-verified="handleRequestVerified"
        />
      </box>

      <!-- Observations -->
      <box :title="t('Observações')" style="margin-top: 16px;">
        <input-text
          v-model="observations"
          :label="t('Observações sobre o item')"
          :placeholder="t('Descreva o estado do item, observações relevantes...')"
          elem-id="exit-observations"
          data-testid="exit-report-observations"
        />
      </box>

      <!-- Confirmation -->
      <box :title="t('Confirmação')" style="margin-top: 16px;">
        <div
          class="confirmation-check"
          data-testid="exit-report-confirmation-check"
          @click="isConfirmed = !isConfirmed"
        >
          <i :class="isConfirmed ? 'pi pi-check-square' : 'pi pi-stop'"></i>
          <span>{{ t('Confirmo que o item foi inspecionado e as informações estão corretas') }}</span>
        </div>
      </box>

    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { eventBus } from '../../eventBus';
import { config } from '../../services/configService';
import { useSOExitReports } from '../../composables/useSOExitReports';
import { useSOReportAttributeValues } from '../../composables/useSOReportAttributeValues';
import { useSOOrderItemRequests } from '../../composables/useSOOrderItemRequests';
import { useSOProducao } from '../../composables/useSOProducao';
import { useSOReprocessos } from '../../composables/useSOReprocessos';
import { log, error as logError } from '../../utils/DebugLogger';
import SOReprocessoSection from '../../domains/so-orders/SOReprocessoSection.vue';
import SOReportAttributesList from '../../domains/so-orders/SOReportAttributesList.vue';
import type { ReportAttribute, ReportAttributeId as Id } from '../../domains/so-orders/reportAttribute';

interface OrderItemRequest {
  so_oir_id?: Id;
  so_oir_verified?: number | string;
  [key: string]: unknown;
}

interface OrderItemStep {
  so_work_order_step_id: Id;
  so_work_order_step_status?: string;
  so_work_order_step_name?: string;
  so_work_order_step_production_step_id?: Id;
  [key: string]: unknown;
}

interface ExitReportData {
  order_item_id?: Id;
  status?: string;
  qr_code?: string;
  brand?: string;
  model?: string;
  client_name?: string;
  order_id?: Id;
  observations?: string;
  final_status?: string;
  [key: string]: unknown;
}

interface AttributeFetchResult {
  definitions?: ReportAttribute[];
  values?: { so_rav_attribute_id?: Id; so_rav_value?: unknown }[];
}

interface RequestVerifiedPayload {
  id: Id;
  order_item_id: Id;
}

const props = withDefaults(defineProps<{
  orderItemId?: Id | null;
  embedded?: boolean;
  editableByDefault?: boolean;
  requireProductionComplete?: boolean;
}>(), {
  embedded: false,
  editableByDefault: false,
  requireProductionComplete: false,
});

const { t } = useI18n();
const route = useRoute();
const { getExitReport, confirmExitReport } = useSOExitReports();
const { fetchLaudoAttributes, saveLaudoAttributeValues } = useSOReportAttributeValues();
const { fetchByOrderItem: fetchRequests } = useSOOrderItemRequests();
const { fetchOrderItemSteps } = useSOProducao();
const { reprocessos: reprocessosRef, listByOrderItem: listReprocessos } = useSOReprocessos();

const effectiveOrderItemId = computed<Id | undefined>(() => (props.orderItemId ?? (route.params.orderItemId as Id | undefined)) || undefined);

const isPageLoading = ref<boolean>(true);
const isSaving = ref<boolean>(false);
const reportData = ref<ExitReportData | null>(null);
const observations = ref<string>('');
const finalStatus = ref<string>('ready_for_delivery');
const isConfirmed = ref<boolean>(false);
const reportAttributes = ref<ReportAttribute[]>([]);
const reportAttributesEditable = ref<boolean>(props.editableByDefault);
const requests = ref<OrderItemRequest[]>([]);
const allSteps = ref<OrderItemStep[]>([]);
const completedSteps = ref<OrderItemStep[]>([]);

const productionCompleted = computed<boolean>(() =>
  allSteps.value.length > 0
  && allSteps.value.every(
    (s) => s.so_work_order_step_status === 'completed' || s.so_work_order_step_status === 'skipped'
  )
);

const editToggleDisabled = computed<boolean>(() =>
  props.requireProductionComplete && !productionCompleted.value
);

watch(editToggleDisabled, (locked) => {
  if (locked && reportAttributesEditable.value) {
    log('PDVExitReportPage', 'editToggleDisabled', 'forcing reportAttributesEditable=false (production incomplete)');
    reportAttributesEditable.value = false;
  }
}, { immediate: true });

const handleEditableUpdate = (val: boolean): void => {
  if (editToggleDisabled.value) {
    log('PDVExitReportPage', 'handleEditableUpdate', 'ignored — toggle locked (production incomplete)');
    return;
  }
  reportAttributesEditable.value = val;
};

const hasRequiredReportAttributeErrors = computed(() =>
  reportAttributes.value.some((attribute: ReportAttribute) => {
    const isRequired = attribute.so_cra_required === 1 || attribute.so_cra_required === '1' || attribute.required === 1 || attribute.required === '1';
    return isRequired && !attribute.value;
  })
);

const hasUnconfirmedExitRequests = computed(() =>
  requests.value.some((r: OrderItemRequest) => Number((r as any).so_oir_exit_confirmed) !== 1)
);

const hasPendingReprocessos = computed<boolean>(() =>
  reprocessosRef.value.some((r) => (r as { so_reprocesso_status?: string }).so_reprocesso_status === 'pending')
);

const reprocessosEditable = computed<boolean>(() => {
  if (editToggleDisabled.value) return false;
  return reportData.value?.status !== 'confirmed' || reportAttributesEditable.value;
});

const canConfirm = computed<boolean>(() => {
  return !!(
    isConfirmed.value
    && !hasRequiredReportAttributeErrors.value
    && !hasUnconfirmedExitRequests.value
    && !hasPendingReprocessos.value
  );
});

const canSave = computed<boolean>(() =>
  reportData.value?.status === 'confirmed'
  && reportAttributesEditable.value
  && !isSaving.value
);

const analiseLaudosEnabled = computed(() => config.isFeatureEnabled('analiselaudos'));

watch(canConfirm, (val) => {
  log('PDVExitReportPage', 'canConfirm', `canConfirm=${val}`, {
    isConfirmed: isConfirmed.value,
    hasRequiredReportAttributeErrors: hasRequiredReportAttributeErrors.value,
    hasUnconfirmedExitRequests: hasUnconfirmedExitRequests.value,
    attributes: reportAttributes.value.map((a: ReportAttribute) => ({
      id: a.so_ra_id,
      name: a.so_ra_name,
      so_cra_required: a.so_cra_required,
      required: a.required,
      hasValue: !!a.value
    })),
    requests: requests.value.map((r: OrderItemRequest) => ({
      id: r.so_oir_id,
      verified: r.so_oir_verified,
      verifiedNumber: Number(r.so_oir_verified)
    }))
  });
}, { immediate: true });

const loadReport = async (): Promise<void> => {
  const orderItemId = effectiveOrderItemId.value;
  log('PDVExitReportPage', 'loadReport', 'Loading exit report', { orderItemId });
  isPageLoading.value = true;

  try {
    const orderItemIdNum = Number(orderItemId);
    // allSettled so one failing dependency (e.g. no steps yet, no requests) does
    // not blank the whole laudo — attributes must still render.
    const settled = await Promise.allSettled([
      getExitReport(orderItemIdNum),
      fetchLaudoAttributes(orderItemIdNum, 'exit'),
      fetchRequests(orderItemIdNum),
      fetchOrderItemSteps(orderItemIdNum),
      listReprocessos(orderItemIdNum)
    ]);
    const exitReportData = settled[0].status === 'fulfilled' ? (settled[0].value as unknown as ExitReportData | null) : null;
    const attributeData = settled[1].status === 'fulfilled' ? (settled[1].value as unknown as AttributeFetchResult) : { definitions: [], values: [] };
    const requestsData = settled[2].status === 'fulfilled' ? (settled[2].value as unknown as OrderItemRequest[] | null) : [];
    const stepsData = settled[3].status === 'fulfilled' ? (settled[3].value as unknown as OrderItemStep[] | null) : [];
    settled.forEach((r, idx) => {
      if (r.status === 'rejected') {
        logError('PDVExitReportPage', 'loadReport', `Sub-fetch ${idx} failed`, r.reason);
      }
    });

    reportData.value = exitReportData || { order_item_id: orderItemId, status: 'pending' };
    if (exitReportData?.observations) {
      observations.value = exitReportData.observations;
    }
    if (exitReportData?.final_status) {
      finalStatus.value = exitReportData.final_status;
    }

    // Build report attributes with values
    const definitions = attributeData.definitions || [];
    const values = attributeData.values || [];
    const valueMap: Record<string, unknown> = Object.fromEntries(values.map(v => [String(v.so_rav_attribute_id), v.so_rav_value]));

    reportAttributes.value = definitions.map((def: ReportAttribute) => ({
      ...def,
      value: (valueMap[String(def.so_ra_id)] as string | null) || null
    }));

    // Set requests and steps (track all + completed for production-completion gating)
    requests.value = requestsData || [];
    allSteps.value = stepsData || [];
    completedSteps.value = allSteps.value.filter(
      step => step.so_work_order_step_status === 'completed' || step.so_work_order_step_status === 'skipped'
    );

    log('PDVExitReportPage', 'loadReport', 'Loaded report attributes, requests, and steps', {
      attributeCount: reportAttributes.value.length,
      requestCount: requests.value.length,
      stepsCount: completedSteps.value.length
    });
    eventBus.emit('pdv.exit-report.loaded', {
      orderItemId,
      status: reportData.value?.status,
      reportAttributeCount: reportAttributes.value.length
    });
  } catch (err) {
    logError('PDVExitReportPage', 'loadReport', 'Error', err);
    reportData.value = { order_item_id: effectiveOrderItemId.value, status: 'pending' };
    reportAttributes.value = [];
    allSteps.value = [];
    completedSteps.value = [];
    eventBus.emit('pdv.exit-report.loaded', { orderItemId: effectiveOrderItemId.value, status: 'pending', reportAttributeCount: 0, error: true });
  } finally {
    isPageLoading.value = false;
  }
};

const handleRequestVerified = async ({ id, order_item_id }: RequestVerifiedPayload): Promise<void> => {
  log('PDVExitReportPage', 'handleRequestVerified', 'Request verified', { id, order_item_id });

  try {
    const updatedRequests = await fetchRequests(Number(order_item_id)) as unknown as OrderItemRequest[];
    requests.value = updatedRequests;
    log('PDVExitReportPage', 'handleRequestVerified', 'Requests refreshed', { count: updatedRequests.length });
  } catch (err) {
    logError('PDVExitReportPage', 'handleRequestVerified', 'Error refreshing requests', err);
  }
};

const openComparisonModal = (): void => {
  log('PDVExitReportPage', 'openComparisonModal', 'Opening laudos comparison modal', { orderItemId: effectiveOrderItemId.value });
  eventBus.emit('modal.laudos-comparison.open', { orderItemId: Number(effectiveOrderItemId.value) });
};

const handleConfirm = async (): Promise<void> => {
  if (!canConfirm.value) return;

  const orderItemId = effectiveOrderItemId.value;
  log('PDVExitReportPage', 'handleConfirm', 'Confirming exit report', { orderItemId, finalStatus: finalStatus.value });
  isSaving.value = true;

  try {
    // Save report attributes first
    const reportAttributeValues = reportAttributes.value
      .filter((attr: ReportAttribute) => attr.value !== null && attr.value !== undefined)
      .map((attr: ReportAttribute) => ({
        attribute_id: attr.so_ra_id,
        value: attr.value
      }));

    const orderItemIdNum = Number(orderItemId);
    if (reportAttributeValues.length > 0) {
      await saveLaudoAttributeValues(orderItemIdNum, reportAttributeValues, 'exit');
      log('PDVExitReportPage', 'handleConfirm', 'Saved report attributes', { count: reportAttributeValues.length });
    }

    // Then confirm the report
    await confirmExitReport(orderItemIdNum, observations.value, finalStatus.value);

    // Nothing has reached the customer yet: the WhatsApp modal below is where
    // the attendant sends the message, and that send is what gets recorded.
    eventBus.emit('globalMessage.success', t('Laudo de saída confirmado'));
    eventBus.emit('pdv.producao.refresh');
    eventBus.emit('so-orders.saved');

    // WhatsApp trigger for exit report. orderItemId makes the send count as a
    // pickup notification for this item (so /pdv/entregas stops asking to "Avisar").
    eventBus.emit('modal.pdv-enviar-whatsapp.open', {
      tipo: 'saida',
      orderItemId: orderItemIdNum,
      clienteId: 0,
      clienteNome: reportData.value?.client_name ?? '',
      clienteTelefone: (reportData.value as Record<string, unknown>)?.client_phone as string ?? '',
      marcaModelo: [reportData.value?.brand, reportData.value?.model].filter(Boolean).join(' ') || undefined,
      numeroOs: reportData.value?.order_id != null ? String(reportData.value.order_id) : undefined,
    });

    eventBus.emit('modal.pdv-laudos-saida.close');
  } catch (err) {
    logError('PDVExitReportPage', 'handleConfirm', 'Error', err);
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message || '';
    eventBus.emit('globalMessage.error', t('Erro ao confirmar laudo') + ': ' + message);
  } finally {
    isSaving.value = false;
  }
};

const handleSave = async (): Promise<void> => {
  const orderItemId = effectiveOrderItemId.value;
  log('PDVExitReportPage', 'handleSave', 'Saving laudo attributes', { orderItemId });
  isSaving.value = true;

  try {
    const reportAttributeValues = reportAttributes.value
      .filter((attr: ReportAttribute) => attr.value !== null && attr.value !== undefined)
      .map((attr: ReportAttribute) => ({
        attribute_id: attr.so_ra_id,
        value: attr.value
      }));

    const orderItemIdNum = Number(orderItemId);
    if (reportAttributeValues.length > 0) {
      await saveLaudoAttributeValues(orderItemIdNum, reportAttributeValues, 'exit');
    }
    eventBus.emit('globalMessage.success', t('Atributos do laudo salvos'));
    eventBus.emit('so-orders.saved');
  } catch (err) {
    logError('PDVExitReportPage', 'handleSave', 'Error', err);
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message || '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar') + ': ' + message);
  } finally {
    isSaving.value = false;
  }
};

// The foto-depois finalization happens inside the embedded SOReprocessoSection,
// which uses its OWN useSOReprocessos instance. Without this, the page's own
// reprocessos ref stays stale after a reprocesso is finalized/created in-session,
// so hasPendingReprocessos never recomputes, canConfirm never flips, and the
// Confirmar Laudo de Saída button (v-if=canConfirm) never renders. Refresh the
// page's ref on the same eventBus channels SOReprocessoSection already emits.
const refreshReprocessos = (): void => {
  const id = Number(effectiveOrderItemId.value);
  if (!id) return;
  log('PDVExitReportPage', 'refreshReprocessos', 'Refreshing reprocessos after in-session change', { id });
  listReprocessos(id);
};

onMounted(() => {
  loadReport();
  eventBus.on('reprocesso.completed', refreshReprocessos);
  eventBus.on('reprocesso.created', refreshReprocessos);
});

onUnmounted(() => {
  eventBus.off('reprocesso.completed', refreshReprocessos);
  eventBus.off('reprocesso.created', refreshReprocessos);
});

defineExpose({ handleConfirm, canConfirm, isSaving, handleSave, canSave, productionCompleted, editToggleDisabled });
</script>

<style scoped>
.pdv-exit-report-page {
  padding: 0;
  
}

.loading-state {
  text-align: center;
  padding: 60px;
  color: var(--p-text-muted-color);
}

.loading-state i {
  font-size: 2rem;
  display: block;
  margin-bottom: 8px;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: var(--p-surface-100);
  border-radius: 8px;
}

.header-info h3 {
  margin: 0 0 8px 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-compare {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
}

.item-info p {
  margin: 2px 0;
  font-size: 0.875rem;
}


.confirmation-check {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--p-surface-200);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;
}

.confirmation-check:hover {
  border-color: var(--p-primary-color);
}

.confirmation-check i {
  font-size: 1.2rem;
  color: var(--p-primary-color);
}
</style>
